import type { AwardSummary, RequestFacts, StaffContact } from '@myrewards/contracts';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { ReactElement, RefObject } from 'react';
import { meQuery } from '../api';
import { requesterHomeQuery } from '../api/homes';
import { statusOf, summaryAmount } from '../awards/present';
import { TwoColumns, initialsOf } from '../shell';
import {
  ButtonLink,
  Card,
  CardHelp,
  EmptyState,
  StatusPill,
  cx,
  formatShortDate,
  plural,
} from '../ui';
import type { RequesterModel } from './model';
import s from './Home.module.css';

type StageId = NonNullable<AwardSummary['stage']>;

const NEW_REQUEST_HREF = '/recognize';

/** How many requests Home lists; the rest are in My drafts & submitted. */
const SHOWN = 6;

/** The plain stages a request goes through: no employee's choice (no election). */
const TRACK: readonly { id: StageId; label: string }[] = [
  { id: 'draft', label: 'Requested' },
  { id: 'approval', label: 'Approval · Executives decide' },
  { id: 'with_stock_admin', label: 'With the Stock Admin team' },
  { id: 'paid', label: 'Paid' },
  { id: 'closed', label: 'Closed' },
];

function trackIndex(stage: StageId | null): number {
  if (stage === null || stage === 'draft') return 0;
  // An election, if a request ever had one, still sits between the check and payment.
  if (stage === 'employee_choice') return 2;
  return TRACK.findIndex((step) => step.id === stage);
}

/** APP-274: only an Executive bonus is decided outside the app by the Executives. */
const labelOf = (step: (typeof TRACK)[number], request: AwardSummary): string =>
  step.id === 'approval' && request.type !== 'EDB' ? 'Approval' : step.label;

/** Where one request is, along the stages it uses. */
function StageTrack({ request }: { request: AwardSummary }): ReactElement {
  const now = trackIndex(request.stage);
  return (
    <ol className={s.track} aria-label={`Where ${request.displayId ?? 'this request'} is`}>
      {TRACK.map((step, i) => (
        <li
          key={step.id}
          className={cx(s.trackStep, i < now && s.trackDone, i === now && s.trackNow)}
          aria-current={i === now ? 'step' : undefined}
        >
          {i === now ? `${labelOf(step, request)} · now` : labelOf(step, request)}
        </li>
      ))}
    </ol>
  );
}

function listNames(names: readonly string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`;
}

/** "For Dana Park, Jordan Moore and Sam Quinn", and how many more when there are more. */
function forWhom(facts: RequestFacts): string | null {
  const names = facts.recipients.map((p) => p.name).filter((n) => n !== '');
  if (names.length === 0) return null;
  const more = facts.people - names.length;
  return more > 0
    ? `For ${names.join(', ')} and ${plural(more, 'other', 'others')}`
    : `For ${listNames(names)}`;
}

/**
 * R1: where it went and how it ended, in the request's own words: "Sent Oct 12 · one summary
 * notice went to …", then "Letters released Oct 2 · closed Oct 13 · final summary sent to the
 * Executives". Without the facts (an older server), when it was last updated.
 */
function journeyLine(request: AwardSummary, facts: RequestFacts | undefined): string {
  if (facts === undefined || facts.sentAt === null)
    return `${request.stage === 'draft' ? 'Draft · updated' : 'Updated'} ${formatShortDate(request.updatedAt)}`;
  if (request.stage === 'closed' || facts.closedAt !== null) {
    const parts: string[] = [];
    if (facts.lettersReleasedAt !== null)
      parts.push(`Letters released ${formatShortDate(facts.lettersReleasedAt)}`);
    if (facts.closedAt !== null)
      parts.push(`${parts.length > 0 ? 'closed' : 'Closed'} ${formatShortDate(facts.closedAt)}`);
    if (facts.finalSummarySent) parts.push('final summary sent to the Executives');
    if (parts.length > 0) return parts.join(' · ');
  }
  const sent = `Sent ${formatShortDate(facts.sentAt)}`;
  if (request.ended !== null) return sent;
  const to = facts.noticeTo.map((p) => p.name);
  const notice =
    to.length > 0
      ? `one summary notice went to ${listNames(to)}`
      : 'the summary notice goes to the Executives';
  const letters =
    facts.lettersReleasedAt === null
      ? ''
      : ` · letters released ${formatShortDate(facts.lettersReleasedAt)}`;
  return `${sent} · ${notice}${letters}`;
}

function RequestItem({
  request,
  facts,
  waitingOnYou,
}: {
  request: AwardSummary;
  /** Who it's for, when it went and how it ended (`GET /home/requester`). */
  facts: RequestFacts | undefined;
  /** The Executives decided outside the app and it's waiting on this person to record it. */
  waitingOnYou: boolean;
}): ReactElement {
  const status = statusOf(request);
  const live = request.ended === null && request.stage !== 'closed';
  const name = request.displayId ?? 'Draft request';
  const who = facts === undefined ? null : forWhom(facts);
  const parts = [plural(request.people, 'person', 'people'), summaryAmount(request)];
  if (request.title !== null) parts.push(request.title);
  return (
    <li className={cx(s.request, live && s.requestLive)}>
      <div className={s.requestHead}>
        <p className={s.requestId}>
          <span className={s.strong}>{name}</span>
          <StatusPill tone={status.tone} size="sm" label={status.label} />
        </p>
        {/* The decision itself is recorded from Waiting on you, its one place (QA-031). */}
        <ButtonLink
          href={`/awards/${request.id}`}
          variant="ghost"
          size="sm"
          aria-label={`View record of ${name}`}
        >
          View record
        </ButtonLink>
      </div>
      {waitingOnYou && (
        <p className={s.requestWaiting}>
          <StatusPill tone="waiting" size="sm" label="Waiting on you" />
          <span className={s.meta}>Record the Executives’ decision from Waiting on you.</span>
        </p>
      )}
      {who !== null && <p className={s.requestWho}>{who}</p>}
      <p className={s.cardText}>{parts.join(' · ')}</p>
      <p className={s.meta}>{journeyLine(request, facts)}</p>
      {live && <StageTrack request={request} />}
    </li>
  );
}

function MyRequestsCard({
  r,
  decisions,
}: {
  r: RequesterModel;
  decisions: ReadonlyMap<string, string>;
}): ReactElement {
  // Who each is for and where it went: the reader's own requests only (R1, APP-054).
  const side = useQuery(requesterHomeQuery);
  const facts = new Map((side.data?.requests ?? []).map((f) => [f.award.id, f]));
  // Open requests first, then the rest, newest first; the full list is in My drafts & submitted.
  const open = (a: AwardSummary): number => (a.ended === null && a.stage !== 'closed' ? 0 : 1);
  const requests = [...r.requests].sort(
    (a, b) => open(a) - open(b) || b.updatedAt.localeCompare(a.updatedAt),
  );
  const shown = requests.slice(0, SHOWN);
  return (
    <Card
      title="My entries"
      help={{
        shows: `Every award you’ve entered for an Executive in ${String(r.year)} and where each one is now.`,
        canDo:
          'Open a request’s record. When the Executives decide one, record it from Waiting on you.',
      }}
      aside={
        requests.length > 0 ? (
          <span className={s.meta}>
            {`${plural(requests.length, 'award', 'awards')} in ${String(r.year)}`}
          </span>
        ) : undefined
      }
    >
      {requests.length === 0 ? (
        <EmptyState
          variant="first-day"
          art="award"
          headline="No entries yet"
          body="Your first award shows up here the moment you send it, with where it is from approval through letters and payroll."
          action={{ label: 'Start an award', href: NEW_REQUEST_HREF }}
        />
      ) : (
        <ul className={s.requestList}>
          {shown.map((request) => (
            <RequestItem
              key={request.id}
              request={request}
              facts={facts.get(request.id)}
              waitingOnYou={decisions.has(request.id)}
            />
          ))}
        </ul>
      )}
      {requests.length > SHOWN && (
        <ButtonLink href="/awards" variant="ghost" size="sm">
          {`All ${String(requests.length)} requests in My drafts & submitted`}
        </ButtonLink>
      )}
    </Card>
  );
}

/** Who to ask: the Stock Admin team by name and title, or the team in general words. */
function NeedAHandCard({ team }: { team: readonly StaffContact[] }): ReactElement {
  const ask =
    team.length === 1 && team[0] !== undefined
      ? `Ask ${team[0].name} to record a decision or attach the proof for you.`
      : 'Ask the Stock Admin team to record a decision or attach the proof for you.';
  return (
    <Card
      title="Need a hand?"
      titleSize="sm"
      compact
      tone="dark"
      help={{
        shows: 'Who to ask about a request. The Stock Admin team sees every request in progress.',
        canDo: ask,
      }}
    >
      <p className={s.onDarkText}>
        Stock Admins see every request in progress and can record the decision for you.
      </p>
      {team.length > 0 && (
        <ul className={s.contactList} aria-label="The Stock Admin team">
          {team.map((person) => (
            <li key={person.id} className={s.contactRow}>
              <span className={s.contactAvatar} aria-hidden="true">
                {initialsOf(person.name)}
              </span>
              <span className={s.contactText}>
                <span className={s.leadName}>{person.name}</span>
                <span className={s.onDarkMeta}>{person.title ?? 'Stock Admin team'}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** R1's side: who can help. */
function RequesterSide(): ReactElement {
  const q = useQuery(requesterHomeQuery);
  // APP-274: an Executive's delegate sees no budget, so no pool card, in any state.
  return <NeedAHandCard team={q.data?.stockAdmins ?? []} />;
}

const STEPS = [
  {
    title: 'You request',
    body: 'People, amounts and why. You pick who receives each letter.',
  },
  {
    title: 'One summary notice',
    body: 'We send one summary to every Executive, so nobody has to forward it.',
  },
  {
    title: 'They decide outside the app',
    body: 'By email, in Teams or in person. Nothing for them to click in MyRewards.',
  },
  {
    title: 'Record what was approved',
    body: 'You, or a Stock Admin, update the request to what was approved and attach the proof.',
  },
] as const;

function HowRequestsWork({
  headingRef,
}: {
  headingRef: RefObject<HTMLHeadingElement | null>;
}): ReactElement {
  return (
    <section className={s.plainCard} aria-labelledby="how-requests-work">
      <div className={s.plainCardHead}>
        <h2
          id="how-requests-work"
          ref={headingRef}
          tabIndex={-1}
          className={cx('disp', s.plainCardTitle)}
        >
          How it works
        </h2>
        <CardHelp
          topic="How it works"
          shows="The four steps from your request to the Executives’ decision."
          canDo="Start a request when you’re ready. We send the summary notice to the Executives for you."
        />
      </div>
      <ol className={s.howSteps}>
        {STEPS.map((step, i) => (
          <li key={step.title} className={s.howStep}>
            <span className={s.howNumber} aria-hidden="true">
              {i + 1}
            </span>
            <span className={s.howText}>
              <span className={s.strong}>{step.title}</span>
              <span className={s.meta}>{step.body}</span>
            </span>
          </li>
        ))}
      </ol>
      <p className={s.meta}>
        After that: the Stock Admin team’s check, letters, payroll, and a final summary of people,
        amounts and budget impact to the Executives.
      </p>
    </section>
  );
}

/**
 * R1 · an Executive's delegate's part of Home: what they've entered for the Executive and where
 * each is, who can help, and how requests work.
 */
export function RequesterPanel({
  r,
  decisions,
  howItWorksRef,
}: {
  r: RequesterModel;
  /** Award ID → where to record the Executives' decision, for requests waiting on this person. */
  decisions: ReadonlyMap<string, string>;
  howItWorksRef: RefObject<HTMLHeadingElement | null>;
}): ReactElement {
  const { data: me } = useSuspenseQuery(meQuery);
  const entersExecutiveAwards = me.roles.some(
    (a) => a.role === 'executive_delegate' && a.awardTypes?.includes('EDB') === true,
  );
  return (
    <>
      <TwoColumns
        sideLabel="Who can help"
        main={<MyRequestsCard r={r} decisions={decisions} />}
        side={<RequesterSide />}
      />
      {/* These steps are the Executive award's: they say nothing about a Sector bonus, PAA or TAA. */}
      {entersExecutiveAwards && <HowRequestsWork headingRef={howItWorksRef} />}
    </>
  );
}
